import React from 'react';
import { Avatar, Dropdown, Modal } from 'antd';
import type { MenuProps } from 'antd';
import { DotsThree, Trash } from '@phosphor-icons/react';
import type { SquareComment } from '../types';

export interface SquareCommentItemProps {
  comment: SquareComment;
  currentUserId?: string;
  onDelete: (commentId: string) => void;
  deleting?: boolean;
}

/**
 * 格式化相对时间
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;

  // Fallback to date format
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * 单条评论组件
 */
export const SquareCommentItem: React.FC<SquareCommentItemProps> = ({
  comment,
  currentUserId,
  onDelete,
  deleting = false,
}) => {
  const isOwner = currentUserId && comment.userId === currentUserId;

  const handleDelete = () => {
    Modal.confirm({
      title: '删除评论',
      content: '确定要删除这条评论吗？此操作无法撤销。',
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => onDelete(comment.id),
    });
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'delete',
      label: '删除',
      icon: <Trash size={14} />,
      danger: true,
      onClick: handleDelete,
    },
  ];

  return (
    <div className={`square-comment-item ${deleting ? 'deleting' : ''}`}>
      <Avatar
        src={comment.user.avatarUrl}
        size={32}
        className="square-comment-avatar"
      >
        {comment.user.username?.[0]}
      </Avatar>
      <div className="square-comment-content">
        <div className="square-comment-header">
          <span className="square-comment-author">{comment.user.username}</span>
          <span className="square-comment-time">
            {formatRelativeTime(comment.createdAt)}
          </span>
          {isOwner && (
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <button className="square-comment-more-btn">
                <DotsThree size={18} weight="bold" />
              </button>
            </Dropdown>
          )}
        </div>
        <div className="square-comment-text">{comment.content}</div>
      </div>
    </div>
  );
};

export default SquareCommentItem;
